import { cmsBlockLinks, cmsLinkedDocument } from "./links";
import type {
  SiteCmsBlock,
  SiteCmsBranding,
  SiteCmsMenuItem,
  SiteCmsPageDraft,
  SiteCmsTheme,
} from "../types/entities";

export function defaultCmsBranding(
  theme: SiteCmsTheme = "marine",
): SiteCmsBranding {
  const palette = {
    marine: ["#123d69", "#e9b44c"],
    sable: ["#79573d", "#dec8a4"],
    ardoise: ["#293749", "#a5c4c5"],
  }[theme];
  return {
    primary: palette[0],
    accent: palette[1],
    typography: theme === "sable" ? "editorial" : "modern",
    buttonShape: "rounded",
    layoutMode: "classic",
    logoImageId: null,
    coverImageId: null,
    backgroundImageId: null,
    backgroundPosition: "center",
    coverPosition: "center",
    footerText: "",
    contactEmail: "",
    contactPhone: "",
    contactAddress: "",
  };
}
export function cmsImageReferences(
  pages: readonly SiteCmsPageDraft[],
  branding?: Partial<SiteCmsBranding> | null,
): Set<string> {
  const result = new Set<string>();
  for (const id of [
    branding?.logoImageId,
    branding?.coverImageId,
    branding?.backgroundImageId,
  ])
    if (id) result.add(id);
  for (const page of pages)
    for (const block of page.blocks) {
      if (
        (block.type === "cover" || block.type === "imageText") &&
        block.imageId
      )
        result.add(block.imageId);
      if (block.type === "cover" && block.backgroundImageId)
        result.add(block.backgroundImageId);
      if (block.type === "partners")
        for (const item of block.items)
          if (item.imageId) result.add(item.imageId);
    }
  return result;
}
/** Only resources actually rendered on a published site, not inactive classic backdrop.
 * Keep cmsImageReferences for draft/library retention and deletion checks. */
export function cmsPublicImageReferences(
  pages: readonly SiteCmsPageDraft[],
  branding?: Partial<SiteCmsBranding> | null,
): Set<string> {
  return cmsImageReferences(
    pages,
    branding?.layoutMode === "widescreen"
      ? { ...branding, backgroundImageId: null }
      : branding,
  );
}
export function cmsDocumentReferences(
  pages: readonly SiteCmsPageDraft[],
  menu: readonly SiteCmsMenuItem[],
): Set<string> {
  const result = new Set<string>();
  const visit = (items: readonly SiteCmsMenuItem[]) => {
    for (const item of items) {
      if (item.type === "DOCUMENT") result.add(item.slug);
      visit(item.children);
    }
  };
  visit(menu);
  for (const page of pages)
    for (const block of page.blocks) {
      if (block.type === "documents")
        for (const slug of block.slugs) result.add(slug);
      for (const href of cmsBlockLinks(block)) {
        const slug = cmsLinkedDocument(href);
        if (slug) result.add(slug);
      }
    }
  return result;
}
export function cmsCompetitionReferences(
  pages: readonly SiteCmsPageDraft[],
): number[] {
  return [
    ...new Set(
      pages.flatMap((p) =>
        p.blocks.flatMap((b) => (b.type === "competitions" ? b.ids : [])),
      ),
    ),
  ];
}
export function newCmsSection(type: SiteCmsBlock["type"]): SiteCmsBlock {
  switch (type) {
    case "heading":
      return { type, level: 2, text: "Notre association" };
    case "paragraph":
      return { type, text: "Présentez votre association et vos activités." };
    case "button":
      return { type, label: "En savoir plus", href: "/" };
    case "cover":
      return {
        type,
        title: "La passion nous rassemble",
        text: "Bienvenue sur le site de notre association.",
        imageId: null,
        backgroundImageId: null,
        backgroundPosition: "center",
        label: "Nous découvrir",
        href: "/",
      };
    case "text":
      return {
        type,
        title: "Notre histoire",
        text: "Une **passion commune**, des rencontres et des projets.\n\n- Partager\n- Jouer\n- Se retrouver",
      };
    case "imageText":
      return {
        type,
        title: "Un lieu pour se retrouver",
        text: "Présentez votre terrain et la vie de votre association.",
        imageId: null,
        alt: "",
        side: "left",
      };
    case "info":
      return {
        type,
        title: "Informations pratiques",
        items: [
          {
            label: "Rendez-vous",
            text: "Renseignez ici vos horaires et votre adresse.",
          },
        ],
      };
    case "documents":
      return { type, title: "Documents utiles", slugs: [] };
    case "competitions":
      return { type, title: "Nos concours", ids: [] };
    case "partners":
      return { type, title: "Nos partenaires", items: [] };
    case "cta":
      return {
        type,
        title: "Retrouvons-nous sur le terrain",
        text: "Découvrez nos événements sur BoulesConnect.",
        label: "Voir BoulesConnect",
        href: "https://boulesconnect.fr/",
      };
  }
}
/** Private draft pages never enter a released tree or its navigation/resources. */
export function cmsPublicContent(
  pages: readonly SiteCmsPageDraft[],
  menu: readonly SiteCmsMenuItem[],
): { pages: SiteCmsPageDraft[]; headerMenu: SiteCmsMenuItem[] } {
  const visible = pages.filter((page) => page.visibility !== "DRAFT");
  const names = new Set(visible.map((page) => page.slug));
  function visit(items: readonly SiteCmsMenuItem[]): SiteCmsMenuItem[] {
    return items.flatMap((item) => {
      const children = visit(item.children);
      // A private parent is never exposed. Keep only its public descendants.
      if (item.type === "PAGE" && !names.has(item.slug)) return children;
      if (item.type === "GROUP" && !children.length) return [];
      return [{ ...item, children }];
    });
  }
  return { pages: visible, headerMenu: visit(menu) };
}
